import config from '#core/config';
import load from '#core/load';
import cookie from '#core/cookie';
import util from '#core/util';
import hash from '#common/hash';
import { getLocation as fetchIpstackLocation } from '#common/ipstack';
import { normalizeState, matchesState, parseStateOptions } from '#common/geo-match';

// TODO: eventually this should be refactored to use most of the same code as
// the alert, or vice versa

const cookie_prefix = 'modal_shown';
const cookie_value = '1';
const id_attr = 'id';
const click_delay_attr = 'data-modal-click-delay';
const page_delay_attr = 'data-modal-page-delay';
const days_attr = 'data-modal-days';
const idle_attr = 'data-modal-idle';
const is_active_attr = 'data-is-active';
const after_scroll_attr = 'data-modal-after-scroll'; // NEW opt-in
const cookie_name_attr = 'data-modal-cookie-name';
const publish_at_attr = 'data-modal-publish-at';
const unpublish_at_attr = 'data-modal-unpublish-at';
const schedule_valid_attr = 'data-modal-schedule-valid';
const modal_$ = '[uk-modal]';
const location_modal_class = 'location-modal';
const location_modal_active_class = 'location-modal-active';
const location_ready_attr = 'data-location-ready';
const location_pending_attr = 'data-location-pending';
const location_zip_attr = 'data-location-zip';
const location_item_selector = '[data-location-item]';
const location_match_attr = 'data-location-match';
const location_keyword_button_selector = '.js-location-keyword-button';
const location_keyword_base_attr = 'data-keyword-base';
const location_keyword_param = 'keywords';
const true_s = 'true';
const false_s = 'false';
const max_timeout_ms = 2147483647;

const before_show_e = 'beforeshow';
const show_e = 'show';
const shown_e = 'shown';
const before_hide_e = 'beforehide';
const hide_e = 'hide';
const hidden_e = 'hidden';

let has_modal = false;
let user_has_interacted = false;
let $all_modals = null;
const modal_delay_times = new WeakMap();

// after-scroll state
let afterScrollArmed = false;
let afterScrollHandled = false;

const module = {
  init() {
    if (load.ran('modal')) return false;

    load.content(() => {
      hash.setLinkAttribute(modal_$, false);
      has_modal = assign_elements();
      if (!has_modal) return false;
    });
    load.all(() => {
      has_modal = assign_elements();
      if (!has_modal) return false;

      start();
    });
  },
};

export default module;
module.init();

/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
  if (has_modal) return true;

  // TODO: do not store objects on load, account for dynamic content
  $all_modals = document.querySelectorAll(modal_$);
  if (!$all_modals.length) return false;

  $all_modals.forEach($modal => {
    $modal.addEventListener(before_show_e, event => {
      if (
        !is_modal_within_schedule($modal) ||
        (is_location_modal($modal) && !is_location_ready($modal))
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      activate($modal);
    });
    $modal.addEventListener(hidden_e, () => {
      set_modal_cookie($modal);
      deactivate($modal);
    });

    $modal.addEventListener('click', event => {
      if (!event.target) return;
      const button = event.target.closest('.modal__buttons a, .modal__buttons button');
      if (!button) return;
      set_modal_cookie($modal);
    });
  });

  // Keep original "interaction" semantics (click/touch). We do NOT mark scroll as interaction,
  // so `data-modal-idle` still behaves as before.
  ['mousedown', 'touchstart'].forEach(event_type => {
    const click_listener = event => {
      // Ignore clicks that originate inside the location modal so it can be
      // dismissed without blocking other idle-only modals.
      if (event.target && event.target.closest(`.${location_modal_class}`)) {
        return;
      }

      user_has_interacted = true;
      document.removeEventListener(event_type, click_listener);
    };

    document.addEventListener(event_type, click_listener, { passive: true });
  });

  return true;
}

/**
 * Start countdown to showing modal
 */
function start() {
  const modalsAfterScroll = [];
  const modalsImmediate = [];

  $all_modals.forEach($modal => {
    if (!$modal.hasAttribute(page_delay_attr)) return;

    if ($modal.hasAttribute(after_scroll_attr)) {
      modalsAfterScroll.push($modal);
    } else {
      modalsImmediate.push($modal);
    }
  });

  // Original behavior: start timers immediately for normal modals
  modalsImmediate.forEach($modal => {
    const delay = parseFloat($modal.getAttribute(page_delay_attr)) || 0;
    arm_modal_timer($modal, delay);
  });

  // New behavior: wait for first scroll, then start each modal's timer
  if (modalsAfterScroll.length && !afterScrollArmed) {
    arm_after_scroll(modalsAfterScroll);
  }

  // Hash activation unchanged
  setTimeout(() => {
    activate_from_url();
  }, 250);

  // 🔹 NEW: initialize location-based state text in the location modal
  init_location_state();
}

/**
 * Arm "after first scroll" listeners and then start per-modal timers
 */
function arm_after_scroll(modals) {
  afterScrollArmed = true;

  const handler = () => {
    if (afterScrollHandled) return;
    afterScrollHandled = true;

    // Remove listeners once handled
    window.removeEventListener('scroll', handler, { passive: true });
    window.removeEventListener('wheel', handler, { passive: true });
    window.removeEventListener('touchmove', handler, { passive: true });

    // Start each modal's page-delay (default 3s) AFTER the first scroll
    modals.forEach($modal => {
      const delay = parseFloat($modal.getAttribute(page_delay_attr)) || 3;
      arm_modal_timer($modal, delay);
    });
  };

  // Fire on first user scroll gesture (any of these)
  window.addEventListener('scroll', handler, { passive: true, once: false });
  window.addEventListener('wheel', handler, { passive: true, once: false });
  window.addEventListener('touchmove', handler, { passive: true, once: false });
}

/**
 * Arm a modal timer for the later of its page delay or publish boundary.
 * Long waits are checked in safe chunks because browsers cap setTimeout.
 *
 * @param {HTMLElement} $modal
 * @param {number} delay_seconds
 */
function arm_modal_timer($modal, delay_seconds) {
  const delay_at = Date.now() + Math.max(delay_seconds, 0) * 1000;
  modal_delay_times.set($modal, delay_at);

  const show_at = get_modal_show_time($modal, delay_at);
  if (show_at === null) return;

  wait_for_modal_show_time($modal, show_at);
}

function wait_for_modal_show_time($modal, show_at) {
  const remaining = Math.max(show_at - Date.now(), 0);
  const wait = Math.min(remaining, max_timeout_ms);

  setTimeout(() => {
    if (Date.now() < show_at) {
      wait_for_modal_show_time($modal, show_at);
      return;
    }

    show_on_timer($modal);
  }, wait);
}

function get_modal_show_time($modal, delay_at) {
  if (!has_valid_modal_schedule($modal)) return null;

  const publish_at = get_modal_schedule_time($modal, publish_at_attr);
  const unpublish_at = get_modal_schedule_time($modal, unpublish_at_attr);
  const show_at = publish_at === null ? delay_at : Math.max(delay_at, publish_at);

  if (unpublish_at !== null && show_at >= unpublish_at) {
    return null;
  }

  return show_at;
}

function get_modal_schedule_time($modal, attribute) {
  const raw_value = $modal.getAttribute(attribute);
  if (raw_value === null || raw_value === '') return null;

  const timestamp = Number(raw_value);
  return Number.isFinite(timestamp) ? timestamp * 1000 : null;
}

function has_valid_modal_schedule($modal) {
  return $modal.getAttribute(schedule_valid_attr) !== false_s;
}

function is_modal_within_schedule($modal, now = Date.now()) {
  if (!has_valid_modal_schedule($modal)) return false;

  const publish_at = get_modal_schedule_time($modal, publish_at_attr);
  const unpublish_at = get_modal_schedule_time($modal, unpublish_at_attr);

  if (publish_at !== null && now < publish_at) return false;
  if (unpublish_at !== null && now >= unpublish_at) return false;

  return true;
}

/**
 * Show modal automatically after delay
 * @param {HTMLElement} $modal
 */
function show_on_timer($modal) {
  if (!is_modal_within_schedule($modal)) return false;

  if (is_location_modal($modal) && !is_location_ready($modal)) {
    $modal.setAttribute(location_pending_attr, true_s);
    return false;
  }

  const modal_id = $modal.getAttribute(id_attr);
  const cookie_key = get_modal_cookie_key($modal);
  const cookie_name = get_cookie_name(cookie_key);
  const days_between_showing = get_modal_days($modal);
  const show_only_when_idle = $modal.hasAttribute(idle_attr);
  const force_cookie = util.get_query('modal');

  if (
    (
      has_cookie(cookie_name) &&
      days_between_showing >= 0 &&
      force_cookie != 'true'
    ) || (
      show_only_when_idle &&
      user_has_interacted
    )
  ) return false;

  show($modal);
}

/**
 * Check URL for target modal and activate
 */
function activate_from_url() {
  const url_hash = window.location.hash.substring(1);
  if (!url_hash.length) return false;

  const $modal = document.querySelector(`#${url_hash}${modal_$}`);
  if (!$modal) return false;

  // Remove modal ID from URL
  history.replaceState(null, null, '#');

  show($modal);
}

/**
 * Show modal
 * @param {HTMLElement} $modal
 */
function show($modal) {
  if (
    !is_modal_within_schedule($modal) ||
    (is_location_modal($modal) && !is_location_ready($modal))
  ) {
    return;
  }

  const delay = parseFloat($modal.getAttribute(click_delay_attr)) || 0;
  setTimeout(() => {
    if (!is_modal_within_schedule($modal)) return;
    UIkit.modal(`#${$modal.id}`).show();
  }, delay * 1000);
}

/**
 * Activate modal before showing
 * @param {HTMLElement} $modal
 */
function activate($modal) {
  $modal.setAttribute(is_active_attr, true_s);
  if (is_location_modal($modal) && !has_active_non_location_modal()) {
    enable_location_background_unlock();
  } else if (!is_location_modal($modal)) {
    disable_location_background_unlock();
  }
}

/**
 * Deactivate modal after hiding
 * @param {HTMLElement} $modal
 */
function deactivate($modal) {
  $modal.setAttribute(is_active_attr, false_s);
  if (is_location_modal($modal)) {
    if (!has_active_location_modal()) {
      disable_location_background_unlock();
    }
  } else if (!has_active_non_location_modal() && has_active_location_modal()) {
    enable_location_background_unlock();
  }
}

/**
 * Evaluate cookie to determine if modal should be shown
 * @param {string} cookie_name
 * @return {boolean}
 */
function has_cookie(cookie_name) {
  const modal_cookie = cookie.read(cookie_name);
  return modal_cookie === cookie_value;
}

/**
 * Get cookie name
 * @param {string} id
 * @return {string}
 */
function get_cookie_name(id) {
  return util.snake(`${config.site_name}_${cookie_prefix}_${id}`);
}

function get_modal_cookie_key($modal) {
  if (!$modal) return '';
  const override = $modal.getAttribute(cookie_name_attr);
  if (override) return override;
  return $modal.getAttribute(id_attr) || '';
}

function set_modal_cookie($modal) {
  if (!$modal) return;
  const cookie_key = get_modal_cookie_key($modal);
  if (!cookie_key) return;

  const cookie_name = get_cookie_name(cookie_key);
  const days_between_showing = get_modal_days($modal);

  if (
    has_cookie(cookie_name) &&
    days_between_showing >= 0
  ) return;

  cookie.create(cookie_name, cookie_value, days_between_showing);
}

/**
 * Initialize the location modal: pick which of its `[data-location-item]`
 * rows (if any) matches the visitor's IPStack-resolved state, reveal only
 * that one, and fill in its `.js-user-state` text + CTA link. If a row has
 * no state targeting at all, it is the only item in the repeater (rule: show
 * to everyone) and is revealed immediately without waiting on geolocation.
 * If nothing matches, the modal is never marked ready and stays hidden.
 */
function init_location_state() {
  const $location_modal = document.querySelector(`.${location_modal_class}`);
  if (!$location_modal) return;

  const $items = Array.from($location_modal.querySelectorAll(location_item_selector));
  if (!$items.length) return;

  const $always_item = $items.find($item => $item.getAttribute(location_match_attr) === true_s);

  if ($always_item) {
    apply_location_item_settings($location_modal, $always_item);
    reveal_location_item($location_modal, $always_item);
    mark_location_modal_ready($location_modal);
  }

  fetchIpstackLocation()
    .then(location => {
      if (!location || !location.state) return;

      const normalizedState = normalizeState(location.state);
      const $matched = $always_item || $items.find(
        $item => matchesState(parseStateOptions($item.getAttribute('data-state-options')), normalizedState)
      );

      if (!$matched) return;

      if (!$always_item) {
        apply_location_item_settings($location_modal, $matched);
        reveal_location_item($location_modal, $matched);
        mark_location_modal_ready($location_modal, location.zip);
      } else if (location.zip) {
        $location_modal.setAttribute(location_zip_attr, location.zip);
      }

      fill_location_state_text($matched, location.state);
      if (location.zip) {
        update_location_keyword_links($matched, location.zip);
      }
    })
    .catch(err => {
      console.warn('Unable to fetch IPStack location', err);
    });
}

/**
 * Apply the matched location item's campaign schedule and cookie identity to
 * the modal so the shared modal lifecycle can enforce them.
 */
function apply_location_item_settings($modal, $item) {
  [
    cookie_name_attr,
    publish_at_attr,
    unpublish_at_attr,
    schedule_valid_attr,
  ].forEach(attribute => {
    const value = $item.getAttribute(attribute);
    if (value === null) {
      $modal.removeAttribute(attribute);
    } else {
      $modal.setAttribute(attribute, value);
    }
  });

  rearm_location_modal_for_schedule($modal);
}

/**
 * Add a replacement timer only when the matched item's publish boundary is
 * later than the modal's original page-delay timer.
 */
function rearm_location_modal_for_schedule($modal) {
  const delay_at = modal_delay_times.get($modal);
  if (typeof delay_at !== 'number') return;

  const publish_at = get_modal_schedule_time($modal, publish_at_attr);
  if (publish_at === null || publish_at <= delay_at) return;

  const show_at = get_modal_show_time($modal, delay_at);
  if (show_at !== null) {
    wait_for_modal_show_time($modal, show_at);
  }
}

/**
 * Show one `[data-location-item]` row within the modal and hide the rest.
 */
function reveal_location_item($modal, $item) {
  $modal.querySelectorAll(location_item_selector).forEach($el => {
    $el.hidden = $el !== $item;
  });
}

function fill_location_state_text($item, stateName) {
  const stateEl = $item.querySelector('.js-user-state');
  if (!stateEl) return;
  stateEl.textContent = stateName;
}

function is_location_modal($modal) {
  if (!$modal) return false;
  return $modal.classList.contains(location_modal_class);
}

function has_active_location_modal() {
  return !!document.querySelector(`.${location_modal_class}[${is_active_attr}="${true_s}"]`);
}

function has_active_non_location_modal() {
  return !!document.querySelector(`${modal_$}:not(.${location_modal_class})[${is_active_attr}="${true_s}"]`);
}

function get_modal_days($modal) {
  if (!$modal) return 0;
  const raw_days = $modal.getAttribute(days_attr);
  let days_between_showing = parseFloat(raw_days);
  if (Number.isNaN(days_between_showing) || raw_days === null) {
    days_between_showing = 0;
  } else if (days_between_showing < 0) {
    days_between_showing = 0;
  }
  return days_between_showing;
}

function is_location_ready($modal) {
  if (!$modal) return false;
  return $modal.hasAttribute(location_ready_attr);
}

function mark_location_modal_ready($modal, zip) {
  if (!$modal) return;

  $modal.setAttribute(location_ready_attr, true_s);
  if (zip) {
    $modal.setAttribute(location_zip_attr, zip);
  } else {
    $modal.removeAttribute(location_zip_attr);
  }

  const wasPending = $modal.getAttribute(location_pending_attr) === true_s;
  $modal.removeAttribute(location_pending_attr);

  if (wasPending) {
    show_on_timer($modal);
  }
}

function update_location_keyword_links($modal, zip) {
  if (!$modal || !zip) return;

  const $buttons = $modal.querySelectorAll(location_keyword_button_selector);
  if (!$buttons.length) return;

  $buttons.forEach($button => {
    const currentHref = $button.getAttribute(location_keyword_base_attr) || $button.getAttribute('href');
    if (!currentHref) return;

    if (!$button.getAttribute(location_keyword_base_attr)) {
      $button.setAttribute(location_keyword_base_attr, currentHref);
    }

    try {
      const url = new URL(currentHref, get_window_origin());
      url.searchParams.set(location_keyword_param, zip);
      $button.setAttribute('href', url.toString());
    } catch (err) {
      console.warn('Unable to update location button URL', err);
    }
  });
}

function get_window_origin() {
  if (window.location.origin) {
    return window.location.origin;
  }

  return `${window.location.protocol}//${window.location.host}`;
}

function enable_location_background_unlock() {
  const root = document.documentElement;
  const body = document.body;
  if (!root || !body) return;

  root.classList.add(location_modal_active_class);
  body.classList.add(location_modal_active_class);

  root.style.removeProperty('overflow');
  root.style.removeProperty('overflow-y');
  body.style.removeProperty('overflow');
  body.style.removeProperty('overflow-y');
}

function disable_location_background_unlock() {
  const root = document.documentElement;
  const body = document.body;
  if (!root || !body) return;

  root.classList.remove(location_modal_active_class);
  body.classList.remove(location_modal_active_class);
}
